import { IconButton, Tag, Tooltip } from '@ads/components-react';
import { ArrowDownTrayIcon, ArrowPathIcon, TicketIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';

import type { IndividualPaymentPaymentSerialViewModel } from '@/shared/api/generated';
import { Skeleton } from '@/shared/ui/atoms';

import { useParcelSerials } from '../../../hooks/useParcelSerials';
import type { ParcelOperation } from '../../../types/parcels';
import { formatContractDate } from '../../../utils/formatContractDate';
import { EMPTY_VALUE } from '../../../utils/formatCurrency';
import { formatDateTime } from '../../../utils/formatDateTime';
import { CopyableField } from '../../molecules/CopyableField';

export interface ParcelSerialsPanelProps {
  readonly parcelId: number;
  readonly isOpen: boolean;
  /**
   * Ações por boleto — reintegrar a parcela e baixar o PDF do boleto.
   *
   * O `serial` identifica o boleto da linha e vai adiante na request de
   * `reintegrate`, que o aceita para escolher o alvo. Só o download não passa por
   * aqui: a URL do PDF vem na própria resposta de `serials`.
   */
  readonly onOperation: (operation: ParcelOperation, serial?: number) => void;
  /**
   * Exibe o ícone de reintegrar em cada boleto.
   *
   * Decidido por quem tem a parcela em mãos (ver `canReintegrateBillet`): no
   * legado a célula renderizava `-` para quem não fosse `Master`, ou quando a
   * parcela não estava paga e integrada à FEALQ.
   */
  readonly canReintegrate: boolean;
}

/**
 * Grid da sub-tabela de boletos.
 *
 * Independente do grid da tabela de parcelas: o painel é recuado e delimitado
 * por um card próprio, então alinhar as colunas com as da tabela-mãe não
 * ajudaria — os campos não são os mesmos.
 *
 * Seis colunas em vez de oito: `station` e `typeAmbient` são dois códigos curtos
 * que dividem uma célula, e `cancellationDate` só existe em boleto cancelado, o
 * que a torna uma linha secundária do status em vez de uma coluna quase sempre
 * vazia. Somando ~43rem, a sub-tabela cabe dentro do painel recuado sem scroll.
 */
const SERIAL_GRID = 'grid grid-cols-[8.5rem_7.5rem_6rem_minmax(7rem,1fr)_7.5rem_4.5rem] gap-x-nano';

/** Recuo do painel, alinhado sob a coluna de expansão da tabela-mãe. */
const PANEL_PADDING = 'pl-[3.5rem] pr-mini pt-mini pb-xxs';

function PanelShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation('individualPayment');
  return (
    <div
      className={`${PANEL_PADDING} border-b-hairline border-ctx-layout-border bg-ctx-layout-body`}
    >
      <div className="mb-micro flex items-center gap-nano">
        <TicketIcon className="h-mini w-mini shrink-0 text-ctx-content-placeholder" />
        <span className="ts-subtitle-xxs text-ctx-content-title">{t('parcels.serials.title')}</span>
      </div>
      {children}
    </div>
  );
}

function SerialRow({
  serial,
  isLast,
  canReintegrate,
  onOperation,
}: {
  serial: IndividualPaymentPaymentSerialViewModel;
  isLast: boolean;
  canReintegrate: boolean;
  onOperation: (operation: ParcelOperation, serial?: number) => void;
}) {
  const { t } = useTranslation('individualPayment');

  return (
    <div className={isLast ? '' : 'border-b-hairline border-ctx-layout-border'}>
      <div className={`${SERIAL_GRID} items-center px-micro py-nano`}>
        <span className="truncate ts-paragraph-xxs !font-medium tabular-nums text-ctx-content-title">
          {serial.serial ?? EMPTY_VALUE}
        </span>
        <span className="ts-caption-xxs tabular-nums text-ctx-content-base">
          {formatDateTime(serial.nsuDate)}
        </span>
        <span className="truncate ts-caption-xxs text-ctx-content-base">
          {[serial.station, serial.typeAmbient].filter(Boolean).join('  ·  ') || EMPTY_VALUE}
        </span>
        <span className="truncate ts-caption-xxs text-ctx-content-base">
          {serial.situation ?? EMPTY_VALUE}
        </span>
        <span className="flex min-w-none flex-col items-start gap-quark">
          <Tag
            label={serial.active ? t('parcels.serials.active') : t('parcels.serials.cancelled')}
            size="sm"
            status={serial.active ? 'positive' : 'negative'}
            custom={{ text: 'text-nowrap' }}
          />
          {serial.cancellationDate && (
            <span className="truncate ts-caption-xxxs tabular-nums text-ctx-content-placeholder">
              {t('parcels.serials.cancelledOn', {
                date: formatContractDate(serial.cancellationDate),
              })}
            </span>
          )}
        </span>
        <span className="flex items-center justify-end gap-quark">
          {canReintegrate && (
            <IconButton
              icon={ArrowPathIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('parcels.operations.reintegrate')}
              onClick={() => onOperation('reintegrate', serial.serial)}
            />
          )}
          {/* O PDF do boleto já vem como URL na própria resposta de `serials`, então
              o download não passa por endpoint nenhum — é só abrir. `noopener` porque
              a URL é externa (provedor de pagamento). */}
          <Tooltip
            title={
              serial.pdfFileUrl
                ? t('parcels.operations.downloadPdf')
                : t('parcels.serials.pdfUnavailable')
            }
          >
            <IconButton
              icon={ArrowDownTrayIcon}
              hierarchy="tertiary"
              size="sm"
              ariaLabel={t('parcels.operations.downloadPdf')}
              disabled={!serial.pdfFileUrl}
              onClick={() => {
                if (serial.pdfFileUrl) window.open(serial.pdfFileUrl, '_blank', 'noopener');
              }}
            />
          </Tooltip>
        </span>
      </div>

      {(serial.digitableLine || serial.ticket) && (
        <div className="flex flex-col gap-nano bg-ctx-layout-body px-micro py-micro pl-mini">
          {serial.digitableLine && (
            <CopyableField
              label={t('parcels.serials.digitableLine')}
              value={serial.digitableLine}
            />
          )}
          {serial.ticket && (
            <CopyableField label={t('parcels.serials.ticket')} value={serial.ticket} />
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Painel de boletos de uma parcela, exibido dentro da linha expandida.
 *
 * Cada instância dispara a sua própria requisição
 * (`GET /parcels/{parcelId}/serials`) porque a API não oferece versão em lote.
 * Por isso o carregamento e o erro são locais: uma parcela com falha não impede
 * a leitura das outras nem bloqueia a tabela.
 *
 * A hierarquia do frame `OzPIl` é preservada — cabeçalho "Boletos", card
 * aninhado recuado com a sub-tabela e, sob cada boleto, o bloco de linha
 * digitável e ticket copiáveis.
 */
export function ParcelSerialsPanel({
  parcelId,
  isOpen,
  canReintegrate,
  onOperation,
}: ParcelSerialsPanelProps) {
  const { t } = useTranslation('individualPayment');
  const { serials, isLoading, isFetching, isError } = useParcelSerials(parcelId, isOpen);

  if (!isOpen) return null;

  if (isLoading) {
    return (
      <PanelShell>
        <div className="flex flex-col rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface">
          {Array.from({ length: 2 }, (_, index) => `serial-skeleton-${index}`).map((key, index) => (
            <div
              key={key}
              className={`${SERIAL_GRID} items-center px-micro py-micro${
                index === 0 ? ' border-b-hairline border-ctx-layout-border' : ''
              }`}
            >
              <Skeleton className="h-mini w-[7rem] rounded-sm" />
              <Skeleton className="h-mini w-[6rem] rounded-sm" />
              <Skeleton className="h-mini w-[4.5rem] rounded-sm" />
              <Skeleton className="h-mini w-[6rem] rounded-sm" />
              <Skeleton className="h-xs w-[4.5rem] rounded-pill" />
              <Skeleton className="h-mini w-[3rem] rounded-sm" />
            </div>
          ))}
        </div>
      </PanelShell>
    );
  }

  if (isError) {
    return (
      <PanelShell>
        <p className="rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini py-xxs ts-caption-xxs text-ctx-interactive-destructive">
          {t('parcels.serials.error')}
        </p>
      </PanelShell>
    );
  }

  if (serials.length === 0) {
    return (
      <PanelShell>
        <div className="flex items-center gap-micro rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface px-mini py-xxs">
          <TicketIcon className="h-xxs w-xxs shrink-0 text-ctx-content-placeholder" />
          <div className="flex flex-col gap-quark">
            <span className="ts-paragraph-xxs !font-medium text-ctx-content-title">
              {t('parcels.serials.empty')}
            </span>
            <span className="ts-caption-xxs text-ctx-content-placeholder">
              {t('parcels.serials.emptyHint')}
            </span>
          </div>
        </div>
      </PanelShell>
    );
  }

  return (
    <PanelShell>
      <div
        className={`flex flex-col overflow-hidden rounded-sm border-hairline border-ctx-layout-border bg-ctx-layout-surface${
          isFetching ? ' opacity-medium' : ''
        }`}
      >
        <div
          className={`${SERIAL_GRID} items-center border-b-hairline border-ctx-layout-border bg-ctx-layout-body px-micro py-nano`}
        >
          <span className="ts-subtitle-xxxs text-ctx-content-base">
            {t('parcels.serials.columns.ourNumber')}
          </span>
          <span className="ts-subtitle-xxxs text-ctx-content-base">
            {t('parcels.serials.columns.nsuDate')}
          </span>
          <span className="ts-subtitle-xxxs text-ctx-content-base">
            {t('parcels.serials.columns.station')}
          </span>
          <span className="ts-subtitle-xxxs text-ctx-content-base">
            {t('parcels.serials.columns.situation')}
          </span>
          <span className="ts-subtitle-xxxs text-ctx-content-base">
            {t('parcels.serials.columns.status')}
          </span>
          <span className="sr-only">{t('parcels.serials.columns.actions')}</span>
        </div>

        {serials.map((serial, index) => (
          <SerialRow
            key={serial.id ?? `${serial.serial}-${index}`}
            serial={serial}
            isLast={index === serials.length - 1}
            canReintegrate={canReintegrate}
            onOperation={onOperation}
          />
        ))}
      </div>
    </PanelShell>
  );
}
